/**
 * The onboarding step that introduces the Textbook (Reader).
 *
 * IMPORTANT — this subtree is rendered by `renderToStaticMarkup`, not by the normal React tree.
 * `intro.js-react`'s `Steps.configureIntroJs` serialises `step.intro` to a static HTML string, so
 * there is no ThemeProvider, no Redux store, no Router, and every event handler is stripped.
 * The `<ThemeProvider>` below is load-bearing: without it every themed interpolation reads
 * `undefined` and throws during Home's commit phase, white-screening /home.
 *
 * Never add `useAppSelector`, `useNavigate`, `<Link>` or `onClick` in here. The "Open my textbook"
 * link is a real `<a href>` picked up by a delegated document listener in Home.tsx — so it still
 * navigates (via a full load) even if that listener is ever missing.
 */
import aiTutorImage from 'assets/images/reader-features/ai-tutor.png';
import annotateImage from 'assets/images/reader-features/annotate.png';
import offlineImage from 'assets/images/reader-features/offline.png';
import { FC } from 'react';
import styled, { DefaultTheme, ThemeProvider, css, keyframes } from 'styled-components';
import { respondToHeight, respondToWidth } from 'styles/general/respondTo';

export type ReaderFeature = {
  key: string;
  title: string;
  blurb: string;
  /** A crop of the real control from the reader's toolbar. */
  image: string;
  /** Spans both grid columns. */
  isWide?: boolean;
};

export const READER_FEATURES: ReaderFeature[] = [
  {
    key: 'aiTutor',
    title: 'Ask the AI Tutor',
    blurb:
      "Ask a question and get an answer drawn from the book you're reading, linked to the section it " +
      'came from. Trials get 5 questions; paid plans get 50 a day.',
    image: aiTutorImage,
    isWide: true,
  },
  {
    key: 'annotations',
    title: 'Highlight and take notes',
    blurb:
      'Switch to Annotate to highlight, underline, or note any passage. Everything you mark collects ' +
      'in the Comments panel, so you can filter it or jump straight back to it.',
    image: annotateImage,
  },
  {
    key: 'offline',
    title: 'Read offline',
    blurb: 'Install the reader and your whole textbook stays on your device. No connection needed.',
    image: offlineImage,
  },
];

interface TextbookStepInfoProps {
  /** Passed explicitly: `useTheme()` returns undefined outside the app's provider. */
  theme: DefaultTheme;
  /** Also explicit — the scroll hint has to match the tooltip's own background exactly. */
  isDarkMode: boolean;
  stepCount: number;
  totalSteps: number;
  readerHref: string;
}

const TextbookStepInfo: FC<TextbookStepInfoProps> = ({ theme, isDarkMode, stepCount, totalSteps, readerHref }) => {
  return (
    <>
      {/* @ts-ignore - ThemeProvider type compatibility issue with React 18 */}
      <ThemeProvider theme={theme}>
        <Root>
          <Header>
            <StepTitle>Your full textbooks</StepTitle>
            <StepDescription>Not previews or PDFs. Search them, mark them up, read them offline.</StepDescription>
          </Header>

          <GridViewport>
            <FeatureGrid>
              {READER_FEATURES.map(({ key, title, blurb, image, isWide }) => (
                <Feature key={key} $isWide={Boolean(isWide)}>
                  <ImageChip $isWide={Boolean(isWide)}>
                    <FeatureImage src={image} alt="" />
                  </ImageChip>
                  <FeatureText>
                    <FeatureTitle>{title}</FeatureTitle>
                    <FeatureBlurb>{blurb}</FeatureBlurb>
                  </FeatureText>
                </Feature>
              ))}
            </FeatureGrid>
            <ScrollHint $isDark={isDarkMode} aria-hidden="true" />
          </GridViewport>

          <Footer>
            <StepCount>{`${stepCount} of ${totalSteps}`}</StepCount>
            <OpenReaderLink href={readerHref} data-onboarding-action="open-reader">
              Open my textbook
            </OpenReaderLink>
          </Footer>
        </Root>
      </ThemeProvider>
    </>
  );
};

export default TextbookStepInfo;

const Root = styled.div`
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  /* The vendor .introjs-skipbutton is 45px wide and absolutely pinned top-right. */
  padding-right: 48px;
  margin-bottom: 12px;
`;

const StepTitle = styled.h3`
  ${({ theme: { colors, typography } }) => css`
    color: ${colors.neutrals[1]};
    ${typography.title_5_bold_20};
  `}
`;

const StepDescription = styled.p`
  ${({ theme: { colors, typography } }) => css`
    color: ${colors.neutrals[3]};
    ${typography.body_basic_regular_14};
  `}
  margin-top: 6px;
`;

/**
 * A pure-CSS "there is more below" hint, because this subtree is serialised by
 * renderToStaticMarkup and has no JS to watch scroll position.
 *
 * Three background layers. The chevron and its fade are `scroll`-attached, so they stay pinned
 * to the bottom edge of the viewport box; the opaque cover is `local`-attached, so it rides the
 * content and only slides into view once the reader reaches the end — hiding the hint exactly
 * when there is nothing left to scroll to. (Lea Verou's scroll-shadow trick.)
 *
 * `background` must match the tooltip's own background in pages/Home/style.css
 * (.tooltip-textbook / .tooltip-textbook-dark) — keep the two in sync.
 */
const chevronUrl = (isDark: boolean) =>
  // %22 for the SVG's own attribute quotes, so this stays a plain single-quoted string.
  'url("data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 24 24%22' +
  ' fill=%22none%22 stroke=%22' +
  (isDark ? '%23ADADAD' : '%23848484') +
  '%22 stroke-width=%222.5%22 stroke-linecap=%22round%22 stroke-linejoin=%22round%22%3E' +
  '%3Cpath d=%22m6 9 6 6 6-6%22/%3E%3C/svg%3E")';

/**
 * Auto-hiding the hint: a scroll-driven animation drives ScrollHint's opacity from the grid's
 * own scroll position, which is the only JS-free way to react to scrolling that works in
 * WebKit. FeatureGrid publishes a named scroll timeline, GridViewport puts that name in scope
 * for its subtree, and ScrollHint — a sibling of the scroller, not a descendant — consumes it.
 * A named timeline is required precisely because of that sibling relationship: `scroll(nearest)`
 * resolves against the nearest scrollable ancestor of the ANIMATED element, which here is the
 * page, not the grid.
 *
 * Supported in Chrome/Edge 116+, Safari 26+ and iOS 26+. Not in Firefox, and not on iPhones
 * below iOS 26 — there the @supports block is skipped entirely and the hint stays permanently
 * visible, which is exactly the behaviour this file shipped with before.
 *
 * The doubled parentheses are LOAD-BEARING, not a typo. styled-components v5 preprocesses with
 * @emotion/stylis (a stylis 3.5 fork), which rewrites a bare `@supports (a: b)` condition into
 * `(-webkit-a: b) or (a: b)`. That happens to stay valid for a lone positive condition, but any
 * `not`/`and` form becomes `not (…) or (…)`, which matches no production in the css-conditional-3
 * grammar — so browsers drop the entire rule AND its contents, silently. Wrapping the condition
 * in one extra layer of parens makes stylis pass it through untouched. For the same reason there
 * must never be an `@supports not (…)` branch in this file: write the fallback as the
 * unconditional base style and put only the enhancement inside the gate.
 */
const SUPPORTS_SCROLL_TIMELINE = '((animation-timeline: --readerFeatures))';

const hintFade = keyframes`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
`;

/** Establishes the containing block for ScrollHint, which must sit ABOVE the scrolled cards. */
const GridViewport = styled.div`
  position: relative;
  min-height: 0;

  /* Puts the grid's timeline name in scope for this subtree so ScrollHint can reach it.
     Not inherited, so it has to sit on the nearest common ancestor of grid and hint. */
  @supports ${SUPPORTS_SCROLL_TIMELINE} {
    timeline-scope: --readerFeatures;
  }
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;

  ${respondToWidth.s`
    grid-template-columns: minmax(0, 1fr);
  `}

  /* Deliberately no scroller at normal heights — the grid is sized to fit. Below, only where it
     genuinely has to scroll: very short viewports (landscape phones), and phones, where the
     panel is always the centred floating fallback and five cards cannot fit at a legible size.
     Kept well under the viewport so the panel does not grow to full height. */
  ${respondToHeight.xs`
    max-height: 56vh;
    overflow-y: auto;
    padding-bottom: 34px;
  `}

  ${respondToWidth.s`
    max-height: 54vh;
    overflow-y: auto;
    padding-bottom: 34px;
  `}

  /* Deliberately outside the media queries. A named scroll timeline whose box is not a scroll
     container — or is one with nothing to scroll — is INACTIVE rather than an error, so this is
     inert at the sizes where the grid is not a scroller and needs no media query of its own. */
  @supports ${SUPPORTS_SCROLL_TIMELINE} {
    scroll-timeline-name: --readerFeatures;
    scroll-timeline-axis: block;
  }
`;

/**
 * The "more below" affordance: a fade to the panel's own background plus a chevron.
 *
 * This has to be an overlay rather than a background on the scroller — the cards are opaque, so
 * anything painted in the scroller's background sits behind them and is never seen. (That is what
 * rules out the usual `background-attachment: local` scroll-shadow trick here.) The grid's
 * padding-bottom gives the last card room to scroll clear of the chevron.
 *
 * Where scroll-driven animations exist it fades out as the grid reaches its end; everywhere else
 * it stays visible, as it always has. See SUPPORTS_SCROLL_TIMELINE above.
 *
 * The gradient must match the tooltip's own background in pages/Home/style.css
 * (.tooltip-textbook / .tooltip-textbook-dark) — keep the two in sync.
 */
const ScrollHint = styled.div<{ $isDark: boolean }>`
  display: none;
  position: absolute;
  left: 0;
  /* Clear of the scrollbar gutter. */
  right: 6px;
  bottom: 0;
  height: 46px;
  border-radius: 0 0 12px 12px;
  pointer-events: none;

  ${({ $isDark }) => css`
    background: linear-gradient(
      to top,
      ${$isDark ? '#282828' : '#ffffff'} 42%,
      ${$isDark ? 'rgba(40, 40, 40, 0)' : 'rgba(255, 255, 255, 0)'}
    );

    &::after {
      content: '';
      position: absolute;
      left: 50%;
      bottom: 5px;
      width: 16px;
      height: 16px;
      transform: translateX(-50%);
      background: ${chevronUrl($isDark)} center / contain no-repeat;
    }
  `}

  ${respondToHeight.xs`
    display: block;
  `}

  ${respondToWidth.s`
    display: block;
  `}

  @supports ${SUPPORTS_SCROLL_TIMELINE} {
    /* Hidden is the BASE state here, and that inversion is deliberate. An animation attached to
       an inactive timeline (a grid too short to scroll) has an unresolved local time, so it is
       not "in effect" at all — animation-fill-mode included — and the element simply renders its
       un-animated styles. Basing at 0 therefore means a grid with nothing to scroll shows no
       hint, which is a bug the always-visible version has. The animation supplies the visible
       state for the case that actually needs it. */
    opacity: 0;

    /* Longhands only, never the animation shorthand: animation-timeline and animation-range are
       reset-only sub-properties of it, so an "animation:" shorthand anywhere below this would
       silently drop the whole thing back to a 0s time-based animation. */
    animation-name: ${hintFade};
    animation-duration: auto;
    animation-timing-function: linear;
    animation-fill-mode: both;
    animation-timeline: --readerFeatures;

    /* Percentages are relative to the whole scroll range; the named ranges (entry/exit/cover/
       contain) belong to view timelines and are invalid here. Proportional rather than a pixel
       calc() so there is no degenerate case when the scroll range is short. */
    animation-range-start: 85%;
    animation-range-end: 100%;
  }
`;

/**
 * Deliberately white in both themes, with hardcoded text colours to match. The images are
 * crops of the reader's own light toolbar, so a card that flipped dark would ring every one
 * of them with a halo. The header and footer outside the grid still follow the theme.
 */
const Feature = styled.div<{ $isWide: boolean }>`
  ${({ $isWide }) =>
    $isWide &&
    css`
      display: flex;
      align-items: center;
      gap: 14px;

      /* Single column below this width, so the wide card stacks like every other one. */
      ${respondToWidth.s`
        display: block;
      `}
    `}
  background-color: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.08);
  grid-column: ${({ $isWide }) => ($isWide ? '1 / -1' : 'auto')};
  padding: 9px;
  border-radius: 12px;
`;

const ImageChip = styled.div<{ $isWide: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 52px;
  padding: 0 10px;

  ${({ $isWide }) =>
    $isWide
      ? css`
          flex-shrink: 0;
          width: 190px;

          ${respondToWidth.s`
            width: auto;
            margin-bottom: 8px;
          `}
        `
      : css`
          margin-bottom: 8px;
        `}
`;

const FeatureImage = styled.img`
  display: block;
  max-width: 100%;
  /* The crops are 40px tall, so this is native size — bigger than this would upscale a 1x
     capture and soften them. */
  max-height: 40px;
  object-fit: contain;
`;

const FeatureText = styled.div`
  min-width: 0;
`;

const FeatureTitle = styled.p`
  ${({ theme: { typography } }) => typography.body_basic_semibold_14};
  color: #333333;
  line-height: 20px;
`;

const FeatureBlurb = styled.p`
  ${({ theme: { typography } }) => typography.subhead_regular_13};
  color: #848484;
  margin-top: 2px;
  line-height: 18px;
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-top: 12px;
`;

const StepCount = styled.span`
  ${({ theme: { colors, typography } }) => css`
    color: ${colors.neutrals[3]};
    ${typography.body_basic_regular_14};
  `}
`;

const OpenReaderLink = styled.a`
  ${({ theme: { colors, typography } }) => css`
    color: ${colors.primary[1]};
    ${typography.body_basic_semibold_14};
  `}
  text-decoration: underline;
  cursor: pointer;
`;
